import React, { useState } from 'react';
import axios from 'axios';
import { previousPowerMethod, heaterMaintenance } from './config';
import RenderError from '../../components/RenderError';
import Success from '../../../equipmentSurveys/components/Success';
import Submit from '../../../equipmentSurveys/components/Submit';

function CurrentPractices({
  applicationData: {
    aid,
    powerMethod,
    powerGenerationSystem,
    hoursPerYear,
    consumptionPerYear,
    fuelCost,
    maintenance,
    maintenanceProvider
  },
  setApplicationData,
  initialView,
  setView
}) {
  const [formData, setFormData] = useState({
    aid: aid ?? '',
    powerMethod: powerMethod ?? {},
    powerGenerationSystem: powerGenerationSystem ?? '',
    hoursPerYear: hoursPerYear ? parseInt(hoursPerYear) : 0,
    consumptionPerYear: consumptionPerYear ?? '',
    fuelCost: fuelCost ? parseFloat(fuelCost) : 0,
    maintenance: maintenance ?? {},
    maintenanceProvider: maintenanceProvider ?? ''
  });
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');

  const handleCheckPowerMethod = (event) => {
    const { value } = event.target;

    if (value in formData.powerMethod) {
      let newFormData = formData.powerMethod;
      delete newFormData[value];
      setFormData({ ...formData, powerMethod: newFormData });
    } else {
      setFormData({
        ...formData,
        powerMethod: {
          ...formData.powerMethod,
          [value]: value === 'Other (please specify)' ? '' : value
        }
      });
    }
  };

  const handleCheckTask = (event) => {
    const { value } = event.target;

    if (value in formData.maintenance) {
      let newFormData = formData.maintenance;
      delete newFormData[value];
      setFormData({ ...formData, maintenance: newFormData });
    } else {
      setFormData({
        ...formData,
        maintenance: { ...formData.maintenance, [value]: '' }
      });
    }
  };

  const setMaintenanceInterval = (event, task) => {
    const { value } = event.target;
    setFormData({
      ...formData,
      maintenance: { ...formData.maintenance, [task]: value }
    });
  };

  const handleInputChange = (event) => {
    const { value, name } = event.target;

    if (name.includes('_other')) {
      let key = name.replace('_other', '');
      setFormData({
        ...formData,
        [key]: {
          ...formData[key],
          ['Other (please specify)']: value
        }
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setLoading(true);
    setSuccess('');
    setErrors([]);

    axios
      .post(
        window.wlPipConfig.ajaxUrl +
          '?action=wl_fip_save_power_generator_current_practices',
        formData
      )
      .then((res) => {
        setLoading(false);
        setSuccess(res.data.data.message);
        setApplicationData(res.data.data.applicationData);
        setTimeout(() => setView(initialView), 1000);
      })
      .catch((err) => {
        setLoading(false);
        setErrors(err.response.data.data);
      });
  };

  return (
    <div className="fip-unit-form">
      <div className="fip-unit-form-fields">
        <div className="grid__col-xs-12 u--padding-left-0">
          <div className="form__field form__field--required">
            <label className="form__label">
              Before participating in the Propane Farm Incentive Program, how
              did you power the site where you plan to install your
              propane-fueled generator? Check all that apply.
            </label>
            {errors.powerMethod && <RenderError error={errors.powerMethod} />}
            {Object.values(previousPowerMethod).map((method) => (
              <div key={method}>
                <label className="form__checkbox">
                  <input
                    type="checkbox"
                    value={method}
                    checked={method in formData.powerMethod}
                    onChange={handleCheckPowerMethod}
                  />
                  {method}
                </label>
                {method in formData.powerMethod &&
                  (method === 'Other (please specify)' ? (
                    <div className="u--margin-top-10 u--margin-left-10">
                      <input
                        type="text"
                        name="powerMethod_other"
                        value={formData.powerMethod[method]}
                        onChange={handleInputChange}
                        className="form__input form__input--lg u--width-100"
                      />
                      {errors[method] && <RenderError error={errors[method]} />}
                    </div>
                  ) : (
                    ''
                  ))}
              </div>
            ))}
          </div>
          <div className="form__field form__field--required">
            <label className="form__label">
              In this section, please provide information about the power
              generation system(s) you previously used at the site where you
              plan to install your incentive propane-fueled generator.
            </label>
            <span className="u--margin-right-10">
              What type of power generation unit did you operate? (e.g.,
              generator, combined heat and power unit)
            </span>
            <input
              type="text"
              value={formData.powerGenerationSystem}
              className="form__input form__input--lg u--margin-bottom-10"
              name="powerGenerationSystem"
              onChange={handleInputChange}
            />
            {errors.powerGenerationSystem && (
              <RenderError error={errors.powerGenerationSystem} />
            )}
            <span className="u--margin-right-10">
              On Average, how many hours did your system operate in a typical
              year?
            </span>
            <input
              type="number"
              value={formData.hoursPerYear}
              className="form__input form__input--lg u--margin-bottom-10"
              name="hoursPerYear"
              onChange={handleInputChange}
            />
            {errors.hoursPerYear && <RenderError error={errors.hoursPerYear} />}
            <span className="u--margin-right-10">
              On average, how much total fuel is used for on-site power
              generation in a typical year?
            </span>
            <input
              type="text"
              value={formData.consumptionPerYear}
              className="form__input form__input--lg u--margin-bottom-10"
              name="consumptionPerYear"
              onChange={handleInputChange}
            />
            {errors.consumptionPerYear && (
              <RenderError error={errors.consumptionPerYear} />
            )}
            <span className="u--margin-right-10">
              What was the average price per unit of fuel during the past year?
            </span>
            <input
              type="number"
              step=".01"
              value={formData.fuelCost}
              className="form__input form__input--lg u--margin-bottom-10"
              name="fuelCost"
              onChange={handleInputChange}
            />
            {errors.fuelCost && <RenderError error={errors.fuelCost} />}
          </div>
          <div className="form__field form__field--required">
            <label className="form__label">
              What type of maintenance do your engines typically require each
              year and how often? (select all that apply)
            </label>
            {errors.maintenance && <RenderError error={errors.maintenance} />}
            {Object.entries(heaterMaintenance.questions).map((task) => (
              <div key={task[0]}>
                <label className="form__checkbox">
                  <input
                    type="checkbox"
                    value={task[0]}
                    checked={task[0] in formData.maintenance}
                    onChange={handleCheckTask}
                  />
                  {task[1]}
                </label>
                {task[0] in formData.maintenance && (
                  <div className="form__select u--margin-top-10 u--margin-left-10">
                    {task[0] !== 'other' ? (
                      <select
                        defaultValue={formData.maintenance[task[0]]}
                        onChange={(event) =>
                          setMaintenanceInterval(event, task[0])
                        }
                      >
                        <option value="">Frequency of maintenance</option>
                        {Object.entries(heaterMaintenance.selectOptions).map(
                          (option) => (
                            <option key={option[0]} value={option[0]}>
                              {option[1]}
                            </option>
                          )
                        )}
                      </select>
                    ) : (
                      <div className="form__field form__field--required u--margin-bottom-0">
                        <textarea
                          className="form__textarea"
                          value={formData.maintenance[task[0]]}
                          name="maintenance_other"
                          onChange={(event) =>
                            setMaintenanceInterval(event, task[0])
                          }
                          placeholder="Please Specify"
                        />
                      </div>
                    )}
                    {errors[task[0]] && <RenderError error={errors[task[0]]} />}
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="form__field form__field--required">
            <label
              className="form__label u--margin-right-20"
              htmlFor="maintenanceProvider"
            >
              Who typically performs engine maintenance? (select one)
            </label>
            {errors.maintenanceProvider && (
              <RenderError error={errors.maintenanceProvider} />
            )}
            <label className="form__radio">
              <input
                type="radio"
                name="maintenanceProvider"
                value="company"
                onChange={handleInputChange}
                checked={
                  formData.maintenanceProvider === 'company' && 'checked'
                }
              />
              My company or employee(s)
            </label>
            <label className="form__radio">
              <input
                type="radio"
                name="maintenanceProvider"
                value="dealership"
                onChange={handleInputChange}
                checked={
                  formData.maintenanceProvider === 'dealership' && 'checked'
                }
              />
              My dealership or local service center
            </label>
          </div>
          <Submit
            loading={loading}
            handleSubmit={handleSubmit}
            text="Save Section"
            errors={errors}
            disabled={!Boolean(aid)}
          />
          {success && <Success success={success} />}
        </div>
      </div>
    </div>
  );
}

CurrentPractices.displayName = 'CurrentPractices'; // because WebPack renames the function
export default CurrentPractices;
